import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Bell, Clock, History, FileText, Wrench, MapPin, MessageSquare, Lightbulb, LogIn, Coffee, Play, CheckCircle2, Target, PhoneCall, TrendingUp, BellRing, Shield, ScanLine, ShieldCheck, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { subscribeToPush, getPushStatus } from '@/utils/pushNotifications';
import AdminMobileDashboard from '@/components/admin/AdminMobileDashboard';

const EmployeeDashboard = ({ auth, onLogout }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [todayEntry, setTodayEntry] = useState(null);
  const [activeOffticket, setActiveOffticket] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [dailyNote, setDailyNote] = useState('');
  const [noteSaved, setNoteSaved] = useState(false);
  const [objectives, setObjectives] = useState(null);
  const [pushStatus, setPushStatus] = useState('loading');
  const [salesDetail, setSalesDetail] = useState(null);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const navigate = useNavigate();

  // Timeclock state
  const [actionLoading, setActionLoading] = useState('');
  const [lastGps, setLastGps] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [todayRes, offticketRes, notifsRes, countRes, settingsRes] = await Promise.all([
        axios.get(`${API}/time/employee/${auth.employee.id}/today`),
        axios.get(`${API}/offticket/active/${auth.employee.id}`),
        axios.get(`${API}/notifications/${auth.employee.id}`),
        axios.get(`${API}/notifications/${auth.employee.id}/count`),
        axios.get(`${API}/settings`).catch(() => ({ data: null })),
      ]);
      setTodayEntry(todayRes.data);
      setActiveOffticket(offticketRes.data);
      setNotifications(notifsRes.data);
      setUnreadCount(countRes.data.count);
      if (settingsRes.data) setCompanyInfo(settingsRes.data);
    } catch (err) {
      console.error(err);
    }
  }, [auth.employee.id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load existing daily note
  useEffect(() => {
    const loadNote = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const res = await axios.get(`${API}/workshop/planning`, { params: { date: today } });
        const me = res.data?.employees?.find(e => e.employee_id === auth.employee.id);
        if (me && me.note_employee) {
          setDailyNote(me.note_employee);
          setNoteSaved(true);
        }
      } catch {}
    };
    loadNote();
  }, [auth.employee.id]);

  // Load objectives for reception/vente roles
  const role = auth.employee.role || '';
  const isSalesRole = ['vente', 'reception', 'reception_vente'].includes(role);

  // Check push notification status
  useEffect(() => {
    getPushStatus().then(setPushStatus).catch(() => setPushStatus('unsupported'));
  }, []);

  const handleEnablePush = async () => {
    try {
      const result = await subscribeToPush(auth.employee.id);
      if (result.subscribed) {
        setPushStatus('subscribed');
        toast.success('Notifications push activees');
      } else if (result.permitted === false) {
        setPushStatus('denied');
        toast.error('Notifications refusees par le navigateur');
      } else if (!result.supported) {
        setPushStatus('unsupported');
        toast.error('Push non supporte sur ce navigateur');
      }
    } catch {
      toast.error('Erreur lors de l\'activation');
    }
  };

  useEffect(() => {
    if (!isSalesRole) return;
    const loadObjectives = async () => {
      try {
        const res = await axios.get(`${API}/sales-dashboard/stats`, { headers: { Authorization: `Bearer ${auth.token}` } });
        setObjectives(res.data);
      } catch {}
    };
    loadObjectives();
  }, [isSalesRole, auth.token]);

  const saveDailyNote = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      await axios.post(`${API}/workshop/daily-note`, { employee_id: auth.employee.id, date: today, employee_note: dailyNote });
      toast.success('Note enregistree');
      setNoteSaved(true);
    } catch { toast.error('Erreur'); }
  };

  const markAllRead = async () => {
    await axios.put(`${API}/notifications/read-all/${auth.employee.id}`);
    setUnreadCount(0);
  };

  const getStatus = () => {
    if (!todayEntry || !todayEntry.clock_in) return 'absent';
    if (todayEntry.clock_out) return 'completed';
    const breaks = todayEntry.breaks || [];
    if (breaks.length > 0 && !breaks[breaks.length - 1].end_time) return 'on_break';
    return 'active';
  };

  const status = getStatus();

  const statusLabel = {
    absent: 'Non pointé',
    active: 'En service',
    on_break: 'En pause',
    completed: 'Journée terminée',
  };

  const statusColor = {
    absent: 'bg-slate-400',
    active: 'bg-emerald-500',
    on_break: 'bg-amber-500',
    completed: 'bg-blue-500',
  };

  // === TIME ACTION WITH GPS ===
  const handleTimeAction = async (actionType) => {
    setActionLoading(actionType);
    try {
      let location = null;
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 })
        );
        location = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        setLastGps(location);
      } catch {}

      const payload = { employee_id: auth.employee.id, action_type: actionType };
      if (location) { payload.latitude = location.latitude; payload.longitude = location.longitude; }

      const res = await axios.post(`${API}/time/action`, payload);
      toast.success(res.data.message);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading('');
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '--:--';
    try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }
    catch { return '--:--'; }
  };

  const tiles = [
    {
      id: 'offticket',
      label: 'Hors Fiche',
      sublabel: 'Nettoyage, dépannage...',
      icon: Wrench,
      iconBg: 'bg-orange-500/20',
      iconColor: 'text-orange-400',
      path: '/employee/offticket',
      badge: activeOffticket ? 'En cours' : null,
      badgeColor: 'bg-orange-500',
    },
    ...(isSalesRole ? [
      {
        id: 'objectives',
        label: 'Mes objectifs',
        sublabel: objectives ? `${objectives.sales_count || 0} ventes ce mois` : 'Objectifs du mois',
        icon: Target,
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        action: 'show-objectives',
      },
      {
        id: 'followups',
        label: 'Relances du jour',
        sublabel: objectives ? `${objectives.followups_today || 0} a faire` : 'Clients a relancer',
        icon: PhoneCall,
        iconBg: 'bg-sky-500/20',
        iconColor: 'text-sky-400',
        action: 'show-followups',
      },
      {
        id: 'performance',
        label: 'Performance',
        sublabel: objectives ? `${objectives.commission_total || 0} CHF` : 'Commissions',
        icon: TrendingUp,
        iconBg: 'bg-rose-500/20',
        iconColor: 'text-rose-400',
        action: 'show-performance',
      },
    ] : []),
    {
      id: 'history',
      label: 'Historique',
      sublabel: 'Mes pointages',
      icon: History,
      iconBg: 'bg-blue-500/20',
      iconColor: 'text-blue-400',
      path: '/employee/history',
    },
    {
      id: 'requests',
      label: 'Mes Demandes',
      sublabel: 'Congés, messages',
      icon: FileText,
      iconBg: 'bg-violet-500/20',
      iconColor: 'text-violet-400',
      path: '/employee/leaves',
    },
    {
      id: 'ideas',
      label: 'Boite a idees',
      sublabel: 'Proposer une idee',
      icon: Lightbulb,
      iconBg: 'bg-amber-500/20',
      iconColor: 'text-amber-400',
      path: '/employee/ideas',
    },
    {
      id: 'scan-wo',
      label: 'Scanner Fiche',
      sublabel: 'Timbrer sur un vehicule',
      icon: QrCode,
      iconBg: 'bg-cyan-500/20',
      iconColor: 'text-cyan-400',
      path: '/employee/scan-wo',
    },
    ...(['admin'].includes(role) ? [{
      id: 'admin-panel',
      label: 'Panel Admin',
      sublabel: 'Gestion du garage',
      icon: Shield,
      iconBg: 'bg-red-500/20',
      iconColor: 'text-red-400',
      action: 'show-admin-dashboard',
    }] : []),
  ];

  if (showAdminDashboard) {
    return <AdminMobileDashboard auth={auth} onBack={() => setShowAdminDashboard(false)} />;
  }

  return (
    <div className="min-h-screen bg-slate-900" onClick={() => showNotifications && setShowNotifications(false)}>
      {/* Header with company logo */}
      <div className="sticky top-0 z-10 border-b border-white/10" style={{ background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(12px)' }}>
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {companyInfo?.has_logo ? (
              <img src={`${API}/settings/logo`} alt="Logo" className="w-10 h-10 rounded-xl object-contain bg-white p-0.5" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                <span className="text-white font-bold text-sm">{(companyInfo?.company?.name || 'V')[0]}</span>
              </div>
            )}
            <div>
              <p className="text-white text-sm font-semibold">{auth.employee.first_name} {auth.employee.last_name}</p>
              <p className="text-slate-400 text-xs">{companyInfo?.company?.name || ''}{companyInfo?.company?.city ? ` — ${companyInfo.company.city}` : ''}</p>
              <p className="text-slate-500 text-[10px]">{format(currentTime, 'EEEE d MMMM', { locale: fr })}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Button
                variant="ghost" size="sm"
                className="text-slate-300 hover:text-white hover:bg-white/10"
                onClick={(e) => { e.stopPropagation(); setShowNotifications(!showNotifications); if (!showNotifications && unreadCount > 0) markAllRead(); }}
                data-testid="notifications-bell"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center" data-testid="notification-count">
                    {unreadCount}
                  </span>
                )}
              </Button>
              {showNotifications && (
                <div className="absolute right-0 top-10 w-80 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-20 max-h-72 overflow-y-auto" data-testid="notifications-dropdown" onClick={(e) => e.stopPropagation()}>
                  <div className="p-3 border-b border-slate-700">
                    <p className="text-sm font-bold text-white">Notifications</p>
                  </div>
                  {notifications.length === 0 ? (
                    <p className="p-4 text-sm text-slate-400 text-center">Aucune notification</p>
                  ) : (
                    notifications.slice(0, 10).map((n) => (
                      <div key={n.id} className={`p-3 border-b border-slate-700/50 ${!n.is_read ? 'bg-blue-900/20' : ''}`}>
                        <p className="text-sm font-medium text-white">{n.title}</p>
                        <p className="text-xs text-slate-400 mt-1">{n.message}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={onLogout} className="text-slate-400 hover:text-white hover:bg-white/10" data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
        {/* Clock + Status */}
        <div className="text-center space-y-2">
          <div className="text-5xl font-mono font-bold text-white tabular-nums tracking-tight">
            {format(currentTime, 'HH:mm:ss')}
          </div>
          <div className="flex items-center justify-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${statusColor[status]} ${status === 'active' || status === 'on_break' ? 'animate-pulse' : ''}`} />
            <span className="text-sm font-medium text-slate-300">{statusLabel[status]}</span>
          </div>
        </div>

        {/* Today quick info */}
        {todayEntry && todayEntry.clock_in && (
          <div className="flex justify-center gap-6 text-center">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Entrée</p>
              <p className="text-lg font-mono font-bold text-emerald-400">{fmtTime(todayEntry.clock_in)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Sortie</p>
              <p className="text-lg font-mono font-bold text-red-400">{fmtTime(todayEntry.clock_out)}</p>
            </div>
            {todayEntry.total_hours != null && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Total</p>
                <p className="text-lg font-mono font-bold text-white">{todayEntry.total_hours.toFixed(1)}h</p>
              </div>
            )}
          </div>
        )}

        {/* ====== POINTAGE SECTION ====== */}
        <Card className="p-4 rounded-2xl border-white/5" style={{ background: 'rgba(30, 41, 59, 0.7)' }}>
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-emerald-400" />
            <p className="text-sm font-semibold text-white">Pointage securise</p>
            <span className="flex items-center gap-1 text-[10px] text-emerald-400">
              <ShieldCheck className="w-3 h-3" /> QR + GPS
            </span>
          </div>

          {/* Today summary */}
          {todayEntry && todayEntry.clock_in && (
            <div className="flex justify-around text-center mb-3 pb-3 border-b border-white/5">
              <div>
                <p className="text-[10px] text-slate-500">Entree</p>
                <p className="text-sm font-mono font-bold text-emerald-400">{fmtTime(todayEntry.clock_in)}</p>
              </div>
              {todayEntry.breaks?.length > 0 && (
                <div>
                  <p className="text-[10px] text-slate-500">Pauses</p>
                  <p className="text-sm font-mono font-bold text-amber-400">{todayEntry.breaks.length}</p>
                </div>
              )}
              {todayEntry.clock_out && (
                <div>
                  <p className="text-[10px] text-slate-500">Sortie</p>
                  <p className="text-sm font-mono font-bold text-red-400">{fmtTime(todayEntry.clock_out)}</p>
                </div>
              )}
              {todayEntry.total_hours != null && (
                <div>
                  <p className="text-[10px] text-slate-500">Total</p>
                  <p className="text-sm font-mono font-bold text-white">{todayEntry.total_hours.toFixed(1)}h</p>
                </div>
              )}
            </div>
          )}

          {/* Status badge */}
          <div className="text-center mb-3">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
              status === 'active' ? 'bg-emerald-900/40 text-emerald-400' :
              status === 'on_break' ? 'bg-amber-900/40 text-amber-400' :
              status === 'completed' ? 'bg-blue-900/40 text-blue-400' :
              'bg-slate-700 text-slate-400'
            }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${statusColor[status]}`} />
              {statusLabel[status]}
            </span>
          </div>

          {/* Scanner button */}
          <Button
            onClick={() => navigate('/employee/timeclock')}
            className="w-full h-14 rounded-xl text-base font-bold bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="open-secure-timeclock-btn"
          >
            <ScanLine className="w-5 h-5 mr-2" />
            Scanner pour pointer
          </Button>
          <p className="text-[10px] text-slate-500 text-center mt-2">Scannez le QR code affiche sur l'ecran du poste</p>
        </Card>

        {/* Active off-ticket banner */}
        {activeOffticket && (
          <div
            className="p-3 rounded-xl bg-orange-500/15 border border-orange-500/30 flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/employee/offticket')}
            data-testid="active-offticket-banner"
          >
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <Wrench className="w-5 h-5 text-orange-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-orange-300">Hors fiche en cours</p>
              <p className="text-xs text-orange-400/70">{activeOffticket.category} {activeOffticket.comment ? `- ${activeOffticket.comment}` : ''}</p>
            </div>
            <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
          </div>
        )}

        {/* Push Notification Prompt */}
        {pushStatus === 'prompt' && (
          <button
            onClick={handleEnablePush}
            className="w-full p-3 rounded-xl bg-blue-600/15 border border-blue-500/30 flex items-center gap-3 transition-all hover:bg-blue-600/25"
            data-testid="enable-push-btn"
          >
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0">
              <BellRing className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-blue-300">Activer les notifications</p>
              <p className="text-xs text-blue-400/70">Recevez les alertes meme hors de l'app</p>
            </div>
          </button>
        )}
        {pushStatus === 'subscribed' && (
          <div className="w-full p-2 rounded-xl flex items-center justify-center gap-2" data-testid="push-active-badge">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-xs text-emerald-400">Notifications push actives</span>
          </div>
        )}

        {/* Tiles Grid */}
        <div className="grid grid-cols-2 gap-3" data-testid="dashboard-tiles">
          {tiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <button
                key={tile.id}
                onClick={() => {
                  if (tile.action === 'show-admin-dashboard') {
                    setShowAdminDashboard(true);
                  } else if (tile.action) {
                    setSalesDetail(tile.action);
                  } else {
                    navigate(tile.path);
                  }
                }}
                className="relative p-5 rounded-2xl text-left transition-all active:scale-[0.97] border border-white/5 hover:border-white/10"
                style={{ background: 'rgba(30, 41, 59, 0.7)' }}
                data-testid={`tile-${tile.id}`}
              >
                {tile.badge && (
                  <span className={`absolute top-3 right-3 ${tile.badgeColor} text-white text-[10px] font-bold px-2 py-0.5 rounded-full`}>
                    {tile.badge}
                  </span>
                )}
                <div className={`w-12 h-12 rounded-xl ${tile.iconBg} flex items-center justify-center mb-3`}>
                  <Icon className={`w-6 h-6 ${tile.iconColor}`} />
                </div>
                <p className="text-white font-semibold text-sm">{tile.label}</p>
                <p className="text-slate-400 text-xs mt-0.5">{tile.sublabel}</p>
              </button>
            );
          })}
        </div>

        {/* Sales Detail Panel */}
        {salesDetail && (
          <div className="rounded-2xl p-5 border border-white/5" style={{ background: 'rgba(30, 41, 59, 0.9)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-base">
                {salesDetail === 'show-objectives' && 'Mes objectifs'}
                {salesDetail === 'show-followups' && 'Relances du jour'}
                {salesDetail === 'show-performance' && 'Performance'}
              </h3>
              <button onClick={() => setSalesDetail(null)} className="text-slate-400 hover:text-white text-sm">Fermer</button>
            </div>
            
            {!objectives ? (
              <p className="text-slate-400 text-sm text-center py-4">Chargement...</p>
            ) : (
              <>
                {salesDetail === 'show-objectives' && (
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 rounded-xl bg-slate-800/50">
                  <span className="text-slate-300 text-sm">Ventes ce mois</span>
                  <span className="text-emerald-400 font-bold text-lg">{objectives.sales_count || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-xl bg-slate-800/50">
                  <span className="text-slate-300 text-sm">Objectif</span>
                  <span className="text-white font-bold text-lg">{objectives.sales_target || 0}</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-3">
                  <div className="bg-emerald-500 h-3 rounded-full transition-all" style={{ width: `${Math.min(100, objectives.sales_target ? (objectives.sales_count / objectives.sales_target) * 100 : 0)}%` }} />
                </div>
              </div>
            )}

            {salesDetail === 'show-followups' && (
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 rounded-xl bg-slate-800/50">
                  <span className="text-slate-300 text-sm">Relances a faire</span>
                  <span className="text-sky-400 font-bold text-lg">{objectives.followups_today || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-xl bg-slate-800/50">
                  <span className="text-slate-300 text-sm">Objectif relances</span>
                  <span className="text-white font-bold text-lg">{objectives.followups_target || 0}</span>
                </div>
              </div>
            )}

            {salesDetail === 'show-performance' && (
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 rounded-xl bg-slate-800/50">
                  <span className="text-slate-300 text-sm">Commissions</span>
                  <span className="text-rose-400 font-bold text-lg">{objectives.commission_total || 0} CHF</span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-xl bg-slate-800/50">
                  <span className="text-slate-300 text-sm">Mois en cours</span>
                  <span className="text-white font-bold text-lg">{objectives.month || '-'}</span>
                </div>
              </div>
            )}
              </>
            )}
          </div>
        )}

        {/* Daily Note */}
        <div className="rounded-2xl p-4 border border-white/5" style={{ background: 'rgba(30, 41, 59, 0.7)' }}>
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-4 h-4 text-blue-400" />
            <p className="text-sm font-semibold text-white">Note du jour</p>
            {noteSaved && <span className="text-[10px] text-emerald-400">Enregistree</span>}
          </div>
          <textarea
            value={dailyNote}
            onChange={e => { setDailyNote(e.target.value); setNoteSaved(false); }}
            placeholder="Qu'avez-vous fait aujourd'hui ? (visible par l'admin)"
            className="w-full bg-white/5 border border-white/10 text-white rounded-xl p-3 text-sm placeholder:text-slate-500 h-20 resize-none"
            data-testid="daily-note-input"
          />
          <div className="flex justify-end mt-2">
            <Button size="sm" onClick={saveDailyNote} disabled={noteSaved} className="text-xs bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-daily-note">
              {noteSaved ? 'Enregistree' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default EmployeeDashboard;
